import _ from 'lodash';
import axiosCall, { getConfig } from 'src/services/axiosHelper';
import { CodeType } from 'src/contracts/codeType';
import { DrgAnalysisCode } from 'src/contracts/drgAnalysis';
import { AnalyseCode } from 'src/contracts/drgAnalyseResponseDto';
import { convertCodes } from 'src/services/converters/GrouperServiceConverter';

export type SearchHistory = string[];

export async function searchCodes(token: string, codeType: CodeType): Promise<DrgAnalysisCode[]> {
  const url = `/grouper/search/${encodeURIComponent(getConfig().visitId)}/${encodeURIComponent(codeType === CodeType.Diagnosis ? 'DIAGNOSIS' : 'PROCEDURE')}?token=${encodeURIComponent(token)}`;
  const data = await axiosCall<AnalyseCode[]>({
    method: 'get',
    url
  });
  return convertCodes(data);
}

export async function addSearchHistory(term?: string) {
  const data = await axiosCall<SearchHistoryResult>({
    method: term ? 'post' : 'get',
    url: '/iodine-grouper-preference/search-history',
    params: { searchText: term ?? '' }
  });
  return data.searchHistoryItems;
}

export async function getCodebooksSearch(book: string, term: string, date: string, options?: string, rootId?: string): Promise<SearchViewRecord[]> {
  const params: object = { date, max_results: 35, options, root_id: rootId };
  const data = await axiosCall<CodebookTermSearchResponse>({
    method: 'get',
    url: `/grouper/${encodeURIComponent(getConfig().facilityCode)}/codebook/book/${encodeURIComponent(book)}/search/${encodeURIComponent(term)}/`,
    params
  });

  return _.defaultTo(_.get(data, 'search_view_records'), _.defaultTo(_.get(data, 'search_view_tabular_records'), []));
}

export async function getCodebooksDetails(book: string, sequenceId: number, date: string, records = 50, sector = 'AROUND'): Promise<BookViewRecord[]> {
  const data = await axiosCall<CodebookDetailResponse>({
    method: 'get',
    url: `/grouper/${encodeURIComponent(getConfig().facilityCode)}/codebook/book/${encodeURIComponent(book)}/details/${encodeURIComponent(sequenceId)}`,
    params: { date: date, records, sector }
  });

  return _.defaultTo(data.book_view_records, []);
}

export async function getCodebooksPCSTable(code: string, date: string) {
  const data = await axiosCall<CodebookPCSTableResponse>({
    method: 'get',
    url: `/grouper/${encodeURIComponent(getConfig().facilityCode)}/codebooks/pcs-table/${encodeURIComponent(code)}`,
    params: { date: date }
  });
  return data;
}

export async function getCodebookPCSSections(date: string) {
  const data = await axiosCall<CodebookPCSTableResponse>({
    method: 'get',
    url: `/grouper/${encodeURIComponent(getConfig().facilityCode)}/codebooks/pcs-sections`,
    params: { date: date }
  });
  return data;
}

export interface CodebookTermSearchResponse {
  search_type: string,
  data_book: string,
  data_date: string,
  requested_term: string,
  search_term: string,
  search_view_records: SearchViewRecord[],
  search_view_tabular_records: SearchViewRecord[],
  valid_partial_code: string
}

export interface SearchViewRecord {
  data_id: number,
  data_sequence: number,
  data_book?: string,
  data_date: string
  text?: string,
  codes?: string[],
  type?: string
  groupable_codes?: string[]
}

export interface CodebookDetailResponse {
  data_book: string,
  data_date: string,
  data_book_title: string,
  data_book_year: number,
  book_view_records: BookViewRecord[]
}

export interface BookViewRecord {
  data_id: number,
  data_sequence: number,
  data_book: string,
  data_date: string,
  record: BookViewRecordEntry
}

export interface BookViewRecordEntry {
  type: string,
  level: number,
  hasinstructionalnotes: boolean,
  subtypes: Subtype[],
  regular_row_codes: string[],
  table_row_codes: TableRowCodes,
  codes: Code[],
  instructional_note_data: InstructionalNoteData,
  content: string,
  hccs: Hcc[],
  regular_note_data: RegularNoteData
  full_path?: {
    full: string
  }
}

export interface TableRowCodes {
  cells: number,
  table: string,
  items: string[][]
}

export interface Code {
  startcode: string
}

export interface Subtype {
  name: string
}

export interface InstructionalNoteData {
  type: string,
  segments: Segment[]
}

export interface Hcc {
  hcc: number,
  description: string,
  version: string
}

export interface RegularNoteData {
  segments: Segment[]
}

export interface Segment {
  type: string,
  level: number,
  content: string
}

export interface CodebookPCSTableResponse {
  columns: Column[],
  section_title?: string,
  data_book_title: string,
  data_book_year: string
}

export interface Column {
  index: number,
  choices: Choice[],
  header: string
}

export interface Choice {
  char: string,
  label: string,
  prefixes?: string[],
  definition?: string
}

export interface SearchHistoryResult {
  searchHistoryItems: SearchHistory
}
